import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { ALL_COMPETITORS } from './data';
import { STIB_BASE } from './data';

function ComparisonCard({ competitor, index }: { competitor: typeof ALL_COMPETITORS[0]; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const winnerCount = competitor.comparisonCategories.filter((c) => c.winner === 'stib').length;
  const tieCount = competitor.comparisonCategories.filter((c) => c.winner === 'tie').length;
  const competitorWins = competitor.comparisonCategories.filter((c) => c.winner === 'competitor').length;

  return (
    <div
      ref={ref}
      className={`bg-white rounded-3xl border border-gray-100 overflow-hidden transition-all duration-700 hover:border-primary/30 hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-heading font-bold text-secondary text-xl mb-1">{competitor.name}</h3>
            <p className="font-body text-sm text-gray-400">{competitor.tagline}</p>
          </div>
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary/10 flex-shrink-0">
            <i className="ri-building-line text-primary text-2xl"></i>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div
              className="h-full bg-primary rounded-full"
              style={{ width: `${(winnerCount / 8) * 100}%` }}
            />
          </div>
          <span className="font-body font-bold text-primary text-sm">{winnerCount}/8 vantagens STIB</span>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="font-heading font-bold text-secondary text-lg">{competitor.yearsInMarket}+</p>
            <p className="font-body text-xs text-gray-400">anos</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="font-heading font-bold text-secondary text-lg">{competitor.geoCoverage.split(' ')[0]}</p>
            <p className="font-body text-xs text-gray-400">cidades</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="font-heading font-bold text-secondary text-lg">{competitorWins}</p>
            <p className="font-body text-xs text-gray-400">vantagens</p>
          </div>
        </div>

        <p className="font-body text-sm text-gray-500 leading-relaxed mb-6 line-clamp-3">
          {competitor.description}
        </p>

        <div className="flex flex-wrap gap-2 mb-6">
          {competitor.strengths.slice(0, 3).map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-3 py-1 rounded-full font-body text-xs">
              <i className="ri-check-line text-green-500 text-xs"></i>
              {s}
            </span>
          ))}
        </div>

        <Link
          to={`/comparativo/${competitor.slug}`}
          className="flex items-center justify-center gap-2 w-full bg-primary text-white py-3.5 rounded-full font-body font-semibold text-sm hover:bg-opacity-90 transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer"
        >
          Ver comparativo completo
          <i className="ri-arrow-right-line text-white text-sm"></i>
        </Link>
      </div>
    </div>
  );
}

export default function ComparativoHubPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: introRef, isVisible: introVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.1 });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

  useEffect(() => {
    document.title = 'STIB vs Concorrentes | Comparativo de Palestra Silenciosa 2026';

    const satisfactionStars = parseFloat(((STIB_BASE.satisfactionRate / 100) * 5).toFixed(1));

    const schemas = [
      {
        id: 'hub-organization',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: STIB_BASE.fullName,
          alternateName: STIB_BASE.name,
          url: siteUrl,
          description: STIB_BASE.keyDifferentiator,
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'São Paulo',
            addressRegion: 'SP',
            addressCountry: 'BR',
          },
        },
      },
      {
        id: 'hub-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Comparativos', item: `${siteUrl}/comparativo` },
          ],
        },
      },
      {
        id: 'hub-itemlist',
        data: {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Comparativos STIB vs Concorrentes',
          description: 'Comparações editoriais detalhadas entre STIB e as principais empresas de palestra silenciosa do Brasil. 8 critérios analisados por comparativo.',
          numberOfItems: ALL_COMPETITORS.length,
          itemListElement: ALL_COMPETITORS.map((comp, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: `STIB vs ${comp.name}`,
            url: `${siteUrl}/comparativo/${comp.slug}`,
          })),
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [siteUrl]);

  return (
    <>
      <Header />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative min-h-[70vh] w-full flex items-center justify-center overflow-hidden mt-[72px]"
      >
        <div className="absolute inset-0">
          <img
            src="/img/stib/congresso-palco-led.webp"
            alt="Comparativo STIB vs Concorrentes"
            className={`w-full h-full object-cover object-center transition-all duration-1000 ${heroVisible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/50 to-black/80"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto w-full">
          <div className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-widest px-5 py-2 rounded-full mb-8 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-scales-3-line text-primary text-sm"></i>
            </div>
            comparativos honestos
          </div>

          <header>
            <h1 className={`font-heading font-extrabold text-4xl md:text-7xl text-white mb-6 tracking-tight leading-tight lowercase transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              stib vs. concorrentes
            </h1>
          </header>

          <p className={`font-body font-light text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            Comparações transparentes e baseadas em dados reais. Veja por que a <strong className="text-white">STIB</strong> é a escolha de <strong className="text-white">780+ empresas</strong> para palestra silenciosa no Brasil.
          </p>

          <div className={`flex flex-wrap items-center justify-center gap-6 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center">
              <p className="font-heading font-bold text-primary text-3xl">4</p>
              <p className="font-body text-white/60 text-sm">concorrentes analisados</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="text-center">
              <p className="font-heading font-bold text-primary text-3xl">32</p>
              <p className="font-body text-white/60 text-sm">critérios comparados</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="text-center">
              <p className="font-heading font-bold text-primary text-3xl">100%</p>
              <p className="font-body text-white/60 text-sm">transparência</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── INTRO ────────────────────────────────────────────── */}
      <section ref={introRef} className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase mb-8 transition-all duration-700 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            por que comparar?
          </h2>
          <p className={`font-body text-lg text-gray-600 leading-relaxed mb-6 transition-all duration-700 delay-100 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Escolher a empresa certa para <strong className="text-secondary">palestra silenciosa</strong> pode fazer a diferença entre um evento memorável e um problema técnico. Por isso criamos comparativos detalhados com dados reais de mercado — sem marketing vazio.
          </p>
          <p className={`font-body text-lg text-gray-600 leading-relaxed transition-all duration-700 delay-200 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Cada comparação avalia <strong className="text-secondary">8 critérios</strong>: especialização, cobertura, equipamentos, experiência, suporte, preço, tecnologia e transparência. O veredito é sempre honesto — em alguns casos o concorrente ganha, e mostramos isso.
          </p>
        </div>
      </section>

      {/* ── STIB QUICK STATS ───────────────────────────────── */}
      <section ref={statsRef} className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Fones em Estoque', value: '5.000+', icon: 'ri-headphone-line' },
              { label: 'Eventos Realizados', value: '500+', icon: 'ri-calendar-event-line' },
              { label: 'Clientes Atendidos', value: '780+', icon: 'ri-briefcase-line' },
              { label: 'Satisfação Média', value: '98%', icon: 'ri-star-line' },
            ].map((stat, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-6 text-center border border-gray-100 transition-all duration-700 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10 mx-auto mb-4">
                  <i className={`${stat.icon} text-primary text-xl`}></i>
                </div>
                <p className="font-heading font-bold text-secondary text-3xl mb-1">{stat.value}</p>
                <p className="font-body text-sm text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARISON CARDS GRID ────────────────────────────── */}
      <section ref={gridRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Comparativos Disponíveis
            </span>
            <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase transition-all duration-700 ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              escolha o comparativo
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {ALL_COMPETITORS.map((competitor, index) => (
              <ComparisonCard key={competitor.slug} competitor={competitor} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARISON TABLE SUMMARY ─────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Resumo Visual
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary lowercase">
              stib vs. todos — resumo rápido
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" role="table" aria-label="Resumo comparativo STIB vs todos os concorrentes">
              <thead>
                <tr className="bg-secondary text-white">
                  <th className="text-left px-6 py-5 font-heading text-sm uppercase tracking-wider rounded-tl-2xl">Critério</th>
                  <th className="text-center px-4 py-5 font-heading text-sm uppercase tracking-wider">STIB</th>
                  {ALL_COMPETITORS.map((c) => (
                    <th key={c.slug} className="text-center px-4 py-5 font-heading text-sm uppercase tracking-wider">{c.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Especialização', getValue: (c: typeof ALL_COMPETITORS[0]) => c.specializationScore },
                  { label: 'Cobertura SP', getValue: (c: typeof ALL_COMPETITORS[0]) => c.geoCoverageScore },
                  { label: 'Equipamentos', getValue: (c: typeof ALL_COMPETITORS[0]) => c.equipmentScore },
                  { label: 'Experiência', getValue: (c: typeof ALL_COMPETITORS[0]) => c.experienceScore },
                  { label: 'Atendimento', getValue: (c: typeof ALL_COMPETITORS[0]) => c.serviceScore },
                  { label: 'Preço', getValue: (c: typeof ALL_COMPETITORS[0]) => c.priceScore },
                  { label: 'Tecnologia', getValue: (c: typeof ALL_COMPETITORS[0]) => c.techScore },
                  { label: 'Suporte', getValue: (c: typeof ALL_COMPETITORS[0]) => c.supportScore },
                ].map((row, i) => (
                  <tr key={i} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="px-6 py-4 font-body font-semibold text-secondary text-sm">{row.label}</td>
                    <td className="px-4 py-4 text-center">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${(row.getValue({ specializationScore: 9, geoCoverageScore: 8, equipmentScore: 8, experienceScore: 5, serviceScore: 7, priceScore: 8, techScore: 7, supportScore: 7 } as any) / 10) * 100}%` }} />
                        </div>
                        <span className="font-body font-bold text-primary text-sm">{row.getValue({ specializationScore: 9, geoCoverageScore: 8, equipmentScore: 8, experienceScore: 5, serviceScore: 7, priceScore: 8, techScore: 7, supportScore: 7 } as any)}</span>
                      </div>
                    </td>
                    {ALL_COMPETITORS.map((c) => (
                      <td key={c.slug} className="px-4 py-4 text-center">
                        <div className="inline-flex items-center gap-2">
                          <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gray-300 rounded-full" style={{ width: `${(row.getValue(c) / 10) * 100}%` }} />
                          </div>
                          <span className="font-body font-bold text-gray-400 text-sm">{row.getValue(c)}</span>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-secondary/5">
                  <td className="px-6 py-5 font-body font-bold text-secondary text-sm">MÉDIA GERAL</td>
                  <td className="px-4 py-5 text-center">
                    <span className="font-body font-bold text-primary text-lg">7.4</span>
                  </td>
                  {ALL_COMPETITORS.map((c) => (
                    <td key={c.slug} className="px-4 py-5 text-center">
                      <span className="font-body font-bold text-gray-500 text-lg">{c.overallScore}</span>
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="font-body text-xs text-gray-400 text-center mt-4">
            Notas de 0 a 10. STIB pontua mais alto em especialização, equipamentos, cobertura e custo-benefício.
          </p>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────── */}
      <section ref={ctaRef} className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-secondary to-secondary/90"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className={`font-heading font-bold text-4xl md:text-5xl text-white lowercase mb-6 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            ainda em dúvida?
          </h2>
          <p className={`font-body text-lg text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-100 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Solicite um orçamento sem compromisso. Compare preço, prazo e condições diretamente com a STIB. Atendemos todo o Brasil com suporte técnico presencial.
          </p>
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-200 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <a
              href="/orcamento"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SOLICITAR ORÇAMENTO
              <i className="ri-arrow-right-line text-white text-lg"></i>
            </a>
            <a
              href="https://wa.me/5511917776227?text=Olá! Vi os comparativos no site e gostaria de solicitar um orçamento."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-full font-body font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-green-400 text-xl"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}